"use client";

import { Link, useLocation } from "react-router-dom";
import {
  Compass,
  Car,
  Map,
  Bell,
  Camera,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Plus, } from '@/ui/icons'
import { Button } from "@/ui/components";
import { Logo } from "@/ui/components";
import { useAuth } from "@/modules/auth";
import { useTheme, type ThemePreference } from "@/contexts/ThemeContext";
import { NotificationTrigger } from "@/ui/components/notifications/NotificationTrigger";
import { cn } from "@/shared/utils";
import { getUserProfileUrl } from "@/shared/utils/userImages";
import { useCreateModal } from "@/contexts/CreateModalContext";
import { useSubscription } from "@/modules/subscriptions";

const SIDEBAR_WIDTH = 72;

const primaryNavItems = [
  { title: "Trips", href: "/trips", icon: Compass, match: (path: string) => path === "/trips" || path.startsWith("/trips/") },
  { title: "Garage", href: "/garage", icon: Car, match: (path: string) => path === "/garage" || path.startsWith("/garage/"), comingSoon: true },
  { title: "Explore", href: "/explore", icon: Map, match: (path: string) => path.startsWith("/explore"), comingSoon: true },
  { title: "Photos", href: "/photos", icon: Camera, match: (path: string) => path === "/photos" || path.startsWith("/photos/") },
  { title: "Notifications", href: null, icon: Bell, match: () => false },
];

function NavLink({
  to,
  icon: Icon,
  title,
  isActive,
  "aria-current": ariaCurrent,
}: {
  to: string;
  icon: React.ElementType;
  title: string;
  isActive: boolean;
  "aria-current"?: "page" | undefined;
}) {
  return (
    <div className="relative group flex items-center justify-center">
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "h-12 w-12 rounded-xl transition-colors",
          isActive
            ? "bg-adventure-600 text-primary-foreground hover:bg-adventure-700 hover:text-primary-foreground"
            : "text-earth-600 hover:bg-earth-200 hover:text-earth-900"
        )}
        asChild
        aria-current={ariaCurrent}
        aria-label={title}
        title={title}
      >
        <Link to={to}>
          <Icon className="h-6 w-6" />
        </Link>
      </Button>
      <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-earth-100 text-earth-900 text-xs font-medium rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
        {title}
      </span>
    </div>
  );
}

const THEME_CYCLE: ThemePreference[] = ["light", "dark", "system"];
const THEME_LABELS: Record<ThemePreference, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

export function AppSidebar() {
  const { user, logout } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const location = useLocation();
  const pathname = location.pathname;
  const { openCreateModal } = useCreateModal();
  const { isPro } = useSubscription();

  const handleSignOut = async () => {
    await logout();
  };

  const cycleTheme = () => {
    const idx = THEME_CYCLE.indexOf(theme);
    setTheme(THEME_CYCLE[(idx + 1) % THEME_CYCLE.length]);
  };

  const userId = user?.id ?? "";
  const ThemeIcon = theme === "system" ? Monitor : resolvedTheme === "dark" ? Moon : Sun;
  const themeTooltip = `Appearance: ${THEME_LABELS[theme]} (click to change)`;

  return (
    <>
      {/* Desktop: floating left sidebar */}
      <aside
        className="hidden md:flex flex-col fixed top-3 left-3 bottom-3 w-[72px] flex-shrink-0 rounded-2xl bg-earth-50/85 dark:bg-earth-100/90 backdrop-blur-xl border border-earth-200/60 dark:border-earth-700/60 shadow-float z-40"
        style={{ width: SIDEBAR_WIDTH }}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Top: Logo */}
        <div className="flex items-center justify-center pt-4 pb-2">
          <Logo to="/dashboard" size="sm" className="h-8 w-8 dark:brightness-0 dark:invert" />
        </div>

        {/* Primary nav */}
        <nav className="flex flex-col items-center gap-1 px-2 flex-1 pt-4">
          {primaryNavItems.map((item) => {
            if (item.title === "Notifications") {
              return (
                <div key={item.title} className="relative group flex items-center justify-center min-h-[48px]">
                  <NotificationTrigger
                    className="h-12 w-12 rounded-xl text-earth-600 hover:bg-earth-200 hover:text-earth-900"
                  />
                  <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-earth-100 text-earth-900 text-xs font-medium rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                    Notifications
                  </span>
                </div>
              );
            }
            const comingSoon = "comingSoon" in item && item.comingSoon;
            if (comingSoon) {
              const Icon = item.icon;
              const tooltip = `${item.title} – Coming soon`;
              return (
                <div key={item.title} className="relative group flex items-center justify-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-12 w-12 rounded-xl text-earth-500 opacity-80 cursor-not-allowed"
                    disabled
                    aria-label={tooltip}
                    title={tooltip}
                  >
                    <Icon className="h-6 w-6" />
                  </Button>
                  <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-earth-100 text-earth-900 text-xs font-medium rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                    {tooltip}
                  </span>
                </div>
              );
            }
            const isActive = item.href ? item.match(pathname) : false;
            return (
              <NavLink
                key={item.title}
                to={item.href!}
                icon={item.icon}
                title={item.title}
                isActive={isActive}
                aria-current={isActive ? "page" : undefined}
              />
            );
          })}
        </nav>

        {/* Create button — its own container */}
        <div className="flex flex-col items-center px-2 pb-2">
          <div className="relative group flex items-center justify-center rounded-2xl bg-earth-100/80 dark:bg-earth-800/60 border border-earth-200/80 dark:border-earth-700/60 p-1.5">
            <Button
              size="icon"
              className="h-12 w-12 rounded-xl bg-adventure-600 text-primary-foreground hover:bg-adventure-700 shadow-sm"
              onClick={openCreateModal}
              aria-label="Create"
            >
              <Plus className="h-6 w-6" />
            </Button>
            <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-earth-100 text-earth-900 text-xs font-medium rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
              Create
            </span>
          </div>
        </div>

        {/* Bottom: Theme, Profile, Logout */}
        <div className="flex flex-col items-center gap-1 p-2 pb-4 border-t border-earth-700">
          <div className="relative group">
            <Button
              variant="ghost"
              size="icon"
              className="h-12 w-12 rounded-xl text-earth-600 hover:bg-earth-200 hover:text-earth-900 dark:text-earth-300 dark:hover:bg-earth-800 dark:hover:text-earth-100"
              onClick={cycleTheme}
              aria-label={themeTooltip}
              title={themeTooltip}
            >
              <ThemeIcon className="h-6 w-6" />
            </Button>
            <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-earth-100 text-earth-900 dark:bg-earth-800 dark:text-earth-100 text-xs font-medium rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
              {themeTooltip}
            </span>
          </div>
          <div className="relative group">
            <Button
              variant="ghost"
              size="icon"
              className="h-12 w-12 rounded-xl text-earth-600 hover:bg-earth-200 hover:text-earth-900 overflow-hidden p-0"
              asChild
              aria-label="Profile"
              title="Profile"
            >
              <Link
                to={`/users/${userId}`}
                className="flex items-center justify-center h-12 w-12 rounded-xl overflow-hidden bg-adventure-500/80 text-primary-foreground font-semibold text-sm"
              >
                {user?.profile_image_path ? (
                  <img
                    src={getUserProfileUrl(user.profile_image_path)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  (user?.user_metadata?.full_name as string)?.slice(0, 2)?.toUpperCase() ??
                  user?.email?.slice(0, 2)?.toUpperCase() ??
                  "?"
                )}
              </Link>
            </Button>
            <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 text-[9px] font-bold rounded-full pointer-events-none leading-none ${isPro ? 'bg-adventure-600 text-white' : 'bg-earth-400 text-white'}`}>
              {isPro ? 'PRO' : 'FREE'}
            </span>
            <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-earth-100 text-earth-900 text-xs font-medium rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
              Profile · {isPro ? 'Pro' : 'Free'}
            </span>
          </div>
          <div className="relative group">
            <Button
              variant="ghost"
              size="icon"
              className="h-12 w-12 rounded-xl text-earth-600 hover:bg-earth-200 hover:text-earth-900"
              onClick={handleSignOut}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-6 w-6" />
            </Button>
            <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-earth-100 text-earth-900 text-xs font-medium rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
              Sign out
            </span>
          </div>
        </div>
      </aside>

      {/* Mobile: bottom nav bar */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 flex flex-row items-center justify-around py-2 px-2 bg-earth-900 border-t border-earth-700 z-40 safe-area-pb"
        role="navigation"
        aria-label="Main navigation"
      >
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "rounded-xl",
            pathname === "/trips" || pathname.startsWith("/trips/")
              ? "bg-adventure-600 text-primary-foreground"
              : "text-earth-200"
          )}
          asChild
          aria-current={pathname === "/trips" || pathname.startsWith("/trips/") ? "page" : undefined}
          aria-label="Trips"
        >
          <Link to="/trips">
            <Compass className="h-6 w-6" />
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl text-earth-300 opacity-80 cursor-not-allowed"
          disabled
          aria-label="Garage – Coming soon"
          title="Garage – Coming soon"
        >
          <Car className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl text-earth-300 opacity-80 cursor-not-allowed"
          disabled
          aria-label="Explore – Coming soon"
          title="Explore – Coming soon"
        >
          <Map className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "rounded-xl",
            pathname === "/photos" || pathname.startsWith("/photos/")
              ? "bg-adventure-600 text-primary-foreground"
              : "text-earth-200"
          )}
          asChild
          aria-current={pathname === "/photos" || pathname.startsWith("/photos/") ? "page" : undefined}
          aria-label="Photos"
        >
          <Link to="/photos">
            <Camera className="h-6 w-6" />
          </Link>
        </Button>
        <div className="relative flex items-center justify-center">
          <NotificationTrigger className="h-12 w-12 rounded-xl text-earth-200" />
        </div>
        <Button
          size="icon"
          className="h-11 w-11 rounded-full bg-adventure-600 text-primary-foreground hover:bg-adventure-700 shadow-md"
          onClick={openCreateModal}
          aria-label="Create"
        >
          <Plus className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl text-earth-200"
          onClick={cycleTheme}
          aria-label={themeTooltip}
          title={themeTooltip}
        >
          <ThemeIcon className="h-6 w-6" />
        </Button>
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl text-earth-200 overflow-hidden p-0"
            asChild
            aria-label="Profile"
          >
            <Link
              to={`/users/${userId}`}
              className="flex items-center justify-center h-10 w-10 rounded-xl overflow-hidden bg-adventure-500/80 text-primary-foreground font-semibold text-xs"
            >
              {user?.profile_image_path ? (
                <img
                  src={getUserProfileUrl(user.profile_image_path)}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                (user?.user_metadata?.full_name as string)?.slice(0, 2)?.toUpperCase() ??
                user?.email?.slice(0, 2)?.toUpperCase() ??
                "?"
              )}
            </Link>
          </Button>
          <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 text-[9px] font-bold rounded-full pointer-events-none leading-none ${isPro ? 'bg-adventure-600 text-white' : 'bg-earth-400 text-white'}`}>
            {isPro ? 'PRO' : 'FREE'}
          </span>
        </div>
      </nav>
    </>
  );
}
